import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { ChatTeardropDots, Star, FlagCheckered, Clock, Sparkle, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import VariableInserter from "@/components/VariableInserter";

// Pills de delay rápido em minutos. "custom" abre o input numérico.
const DELAY_PRESETS = [
  { value: 5, label: "5 min" },
  { value: 10, label: "10 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hora" },
];

const REVIEW_DELAY_PRESETS = null;  // legacy — no longer rendered (iteration 45)
// eslint-disable-next-line no-unused-vars
const _UNUSED_REVIEW_DELAY = REVIEW_DELAY_PRESETS;

const VARIABLES = [
  { token: "{nome_cliente}", label: "Primeiro nome do cliente" },
  { token: "{veiculo}", label: "Veículo/produto do cliente" },
  { token: "{servico_realizado}", label: "Serviço que acabou de ser realizado" },
  { token: "{empresa}", label: "Nome da sua empresa" },
];

const DEFAULT_TPL = (
  "Olá {nome_cliente}! 🚗\n" +
  "Esperamos que tenha gostado do atendimento de hoje na *{empresa}*.\n\n" +
  "Caso tenha qualquer dúvida sobre o serviço realizado, é só me chamar por aqui."
);

const DEFAULT_REVIEW = (
  "Se puder, deixar uma avaliação ajuda muito outros clientes a confiarem no nosso trabalho. ⭐\n\n" +
  "Obrigado por confiar na *{empresa}*."
);

function Toggle({ checked, onChange, "data-testid": testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-slate-900" : "bg-slate-300"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

function DelayPills({ value, presets, onChange, testIdPrefix, unit = "min", maxValue = 1440 }) {
  const isCustom = !presets.find((p) => p.value === value);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {presets.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          data-testid={`${testIdPrefix}-${p.value}`}
          className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${value === p.value && !isCustom ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
        >
          {p.label}
        </button>
      ))}
      <div
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border transition-colors ${
          isCustom
            ? "bg-slate-900 text-white border-slate-900"
            : "bg-white text-slate-700 border-slate-300"
        }`}
      >
        <span className={`text-[11px] ${isCustom ? "text-white/80" : "text-slate-500"}`}>
          Custom
        </span>
        <input
          type="number"
          min="1"
          max={maxValue}
          step="1"
          inputMode="numeric"
          value={isCustom ? value : ""}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            if (Number.isFinite(n) && n >= 1) {
              onChange(Math.min(n, maxValue));
            } else if (e.target.value === "") {
              // permite limpar — não aplica até digitar
            }
          }}
          placeholder="–"
          aria-label={`Tempo personalizado em ${unit === "min" ? "minutos" : "horas"}`}
          title={`Digite um valor de 1 a ${maxValue} ${unit === "min" ? "minutos" : "horas"}`}
          data-testid={`${testIdPrefix}-custom`}
          className={`w-16 bg-transparent text-[12px] font-semibold tabular-nums focus:outline-none placeholder:font-normal text-center ${
            isCustom
              ? "text-white placeholder:text-white/50"
              : "text-slate-900 placeholder:text-slate-400"
          }`}
        />
        <span className={`text-[11px] font-medium ${isCustom ? "text-white/80" : "text-slate-500"}`}>
          {unit}
        </span>
      </div>
    </div>
  );
}

export default function PostService() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/post-service/settings");
      setSettings(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/post-service/settings", {
        enabled: settings.enabled,
        delay_minutes: settings.delay_minutes,
        message_template: settings.message_template,
        ask_review: settings.ask_review,
        review_delay_hours: settings.review_delay_hours,
        review_template: settings.review_template,
      });
      setSettings(data);
      toast.success("Configuração de pós-atendimento salva");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const messageRef = useRef(null);
  const reviewRef = useRef(null);

  if (loading || !settings) {
    return (
      <>
        <Topbar title="Pós-atendimento" subtitle="Mensagem automática após o atendimento ser finalizado." />
        <div className="p-8 text-sm text-slate-500">Carregando…</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title="Pós-atendimento"
        subtitle="Mensagem automática após o operador clicar em Finalizar atendimento."
        actions={
          <button
            onClick={save}
            disabled={saving}
            data-testid="post-service-save"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 text-[12px] font-medium shadow-sm disabled:opacity-60"
          >
            {saving ? "Salvando…" : "Salvar alterações"}
          </button>
        }
      />

      <div className="p-6 md:p-8 max-w-3xl space-y-6">
        {/* Explainer */}
        <section
          data-testid="post-service-explainer"
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 md:p-6"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
              <FlagCheckered size={20} weight="duotone" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-lg font-semibold text-slate-900">
                Fechar o ciclo do atendimento sem esforço operacional
              </h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Quando você clica em <b>Finalizar atendimento</b> num agendamento,
                o AgentUS espera o tempo configurado abaixo e envia uma mensagem
                no WhatsApp do cliente. Você decide o tempo, o texto, e se quer
                pedir uma avaliação depois.
              </p>
            </div>
          </div>
        </section>

        {/* Bloco 1 — Toggle principal + delay */}
        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-slate-900 font-medium">
                <Sparkle size={16} weight="fill" className="text-slate-700" />
                Follow-up pós-atendimento
              </div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Mensagem cordial de agradecimento enviada automaticamente.
              </p>
            </div>
            <Toggle
              checked={!!settings.enabled}
              onChange={(v) => patch("enabled", v)}
              data-testid="post-service-enabled"
            />
          </div>

          <div className={settings.enabled ? "" : "opacity-50 pointer-events-none"}>
            <div className="flex items-center gap-2 text-slate-700 font-medium text-sm mb-3">
              <Clock size={15} weight="fill" className="text-slate-500" />
              Tempo até o envio
            </div>
            <DelayPills
              value={settings.delay_minutes}
              presets={DELAY_PRESETS}
              onChange={(v) => patch("delay_minutes", v)}
              testIdPrefix="post-service-delay"
              unit="min"
              maxValue={1440}
            />

            <div className="mt-5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
                  <ChatTeardropDots size={15} weight="fill" className="text-slate-500" />
                  Mensagem
                </div>
                <button
                  type="button"
                  onClick={() => patch("message_template", DEFAULT_TPL)}
                  className="text-[11px] text-slate-500 hover:text-slate-700"
                  data-testid="post-service-reset-msg"
                >
                  Restaurar padrão
                </button>
              </div>
              <textarea
                ref={messageRef}
                value={settings.message_template || ""}
                onChange={(e) => patch("message_template", e.target.value)}
                rows={6}
                data-testid="post-service-message"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
              <div className="mt-2">
                <VariableInserter
                  textareaRef={messageRef}
                  value={settings.message_template || ""}
                  onChange={(v) => patch("message_template", v)}
                  variables={VARIABLES}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Bloco 2 — Pesquisa de avaliação */}
        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-slate-900 font-medium">
                <Star size={16} weight="fill" className="text-slate-700" />
                Pedir avaliação depois
              </div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Mensagem opcional pedindo feedback ou avaliação no Google.
                Enviada na sequência do follow-up, sem repetir a saudação.
              </p>
            </div>
            <Toggle
              checked={!!settings.ask_review}
              onChange={(v) => patch("ask_review", v)}
              data-testid="post-service-ask-review"
            />
          </div>

          <div className={settings.ask_review ? "" : "opacity-50 pointer-events-none"}>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-4 flex items-start gap-2">
              <Clock size={13} weight="fill" className="text-slate-500 shrink-0 mt-0.5" />
              <p className="text-[12px] text-slate-600 leading-relaxed">
                Enviada <strong className="text-slate-900">na sequência</strong> do follow-up,
                sem repetir a saudação. Sai como continuação natural da mensagem anterior.
              </p>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
                  <ChatTeardropDots size={15} weight="fill" className="text-slate-500" />
                  Mensagem de avaliação
                </div>
                <button
                  type="button"
                  onClick={() => patch("review_template", DEFAULT_REVIEW)}
                  className="text-[11px] text-slate-500 hover:text-slate-700"
                  data-testid="post-service-reset-review"
                >
                  Restaurar padrão
                </button>
              </div>
              <textarea
                ref={reviewRef}
                value={settings.review_template || ""}
                onChange={(e) => patch("review_template", e.target.value)}
                rows={5}
                data-testid="post-service-review-message"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
              <div className="mt-2">
                <VariableInserter
                  textareaRef={reviewRef}
                  value={settings.review_template || ""}
                  onChange={(v) => patch("review_template", v)}
                  variables={VARIABLES}
                />
              </div>

              {/* Preview do bloco que é ANEXADO automaticamente —
                  garante que o operador entenda que o cliente sempre
                  receberá link + opções 1-5, mesmo customizando o
                  template acima. Não-editável de propósito. */}
              <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
                   data-testid="post-service-review-auto-block">
                <div className="flex items-center gap-2 mb-2">
                  <Star size={14} weight="fill" className="text-amber-500" />
                  <span className="text-[12px] font-medium text-slate-700">
                    Bloco anexado automaticamente ao fim da mensagem
                  </span>
                </div>
                <div className="bg-white border border-emerald-100 rounded-md p-2.5 font-mono text-[12px] text-slate-700 leading-relaxed whitespace-pre-wrap">
{`Como você avaliaria seu atendimento? ⭐⭐⭐⭐⭐
👉 Toque para avaliar (leva 5 segundos): https://...

Ou responda aqui mesmo só com o número:
*1* · *2* · *3* · *4* · *5*`}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                  O cliente pode <strong>clicar no link</strong> ou
                  simplesmente <strong>responder com o número</strong>
                  {' '}(também aceita {`"5 estrelas"`}, {`"cinco"`}, {`"⭐⭐⭐⭐⭐"`}).
                  A avaliação é registrada automaticamente nas duas vias.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer informativo */}
        <div className="flex items-start gap-2 text-[12px] text-slate-500 pt-2 pb-8">
          <Info size={14} className="shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            O AgentUS verifica novos atendimentos finalizados a cada 2 minutos.
            Mensagens são enviadas no canal WhatsApp do cliente, abrindo uma
            conversa caso não exista. Se a avaliação chegar ruim, o sistema
            vai criar um alerta interno (em breve).
          </p>
        </div>
      </div>
    </>
  );
}
